import { useLocation, useNavigate } from 'react-router';
import { useSettingsStore } from '../stores/settingsStore';
import { useTheme } from '../theme/useTheme';

export type SidebarNavItem = {
  path: string;
  label: string;
  icon: string;
  locked?: boolean | undefined;
  /** Market-scoped disable. Renders the item dim and non-clickable with a
   *  tooltip explaining the chosen market isn't supported for this surface. */
  disabled?: boolean | undefined;
  /** Optional tooltip shown when `disabled` is true. */
  disabledReason?: string | undefined;
};

export type SidebarNavGroup = {
  label: string;
  items: SidebarNavItem[];
};

export function isSidebarNavItemActive(pathname: string, itemPath: string): boolean {
  return itemPath === '/' ? pathname === '/' : pathname.startsWith(itemPath);
}

export function SidebarNavIcon({ icon, className }: { icon: string; className?: string }) {
  const cls = className ?? 'w-5 h-5';
  switch (icon) {
    case 'grid':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      );
    case 'candlestick':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="9" y1="2" x2="9" y2="6" />
          <rect x="7" y="6" width="4" height="10" />
          <line x1="9" y1="16" x2="9" y2="22" />
          <line x1="17" y1="4" x2="17" y2="8" />
          <rect x="15" y="8" width="4" height="8" />
          <line x1="17" y1="16" x2="17" y2="20" />
        </svg>
      );
    case 'activity':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
        </svg>
      );
    case 'trending':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="23,6 13.5,15.5 8.5,10.5 1,18" />
          <polyline points="17,6 23,6 23,12" />
        </svg>
      );
    case 'crosshair':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="22" y1="12" x2="18" y2="12" />
          <line x1="6" y1="12" x2="2" y2="12" />
          <line x1="12" y1="6" x2="12" y2="2" />
          <line x1="12" y1="22" x2="12" y2="18" />
        </svg>
      );
    case 'wallet':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="6" width="20" height="14" rx="2" />
          <path d="M2 10h20" />
          <rect x="16" y="12" width="4" height="4" rx="1" />
          <path d="M22 6V5a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v1" />
        </svg>
      );
    case 'backtest':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="1,4 1,10 7,10" />
          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
        </svg>
      );
    case 'search':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      );
    case 'filter':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
      );
    case 'key':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
        </svg>
      );
    case 'monitor':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      );
    case 'settings':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
    case 'shield':
      return (
        <svg className={cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
    default:
      return null;
  }
}

function SidebarLockIcon() {
  return (
    <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

export function SidebarMenu({
  groups,
  collapsed,
  onNavigate,
}: {
  groups: SidebarNavGroup[];
  collapsed: boolean;
  onNavigate: (path: string) => void;
}) {
  const location = useLocation();

  return (
    <nav className="flex-1 py-1 px-2 overflow-y-auto">
      {groups.map((group, gi) => (
        <div key={group.label}>
          {/* Group separator */}
          {gi > 0 && <hr className="border-sidebar-border mx-1 my-1.5" />}

          {/* Group label */}
          {!collapsed && (
            <span className="block text-[10px] uppercase tracking-wider text-text-muted px-3 pt-2.5 pb-1 select-none">
              {group.label}
            </span>
          )}

          {/* Items */}
          <div className="space-y-0.5">
            {group.items.map((item) => {
              const active = !item.locked && !item.disabled && isSidebarNavItemActive(location.pathname, item.path);
              const blocked = item.locked || item.disabled;
              const disabledTooltip = item.disabled
                ? (item.disabledReason ?? `${item.label} is unavailable for the selected market`)
                : null;
              const tooltip = collapsed
                ? (item.locked ? `${item.label} (sign in)` : disabledTooltip ?? item.label)
                : (disabledTooltip ?? undefined);

              return (
                <button
                  key={item.path}
                  onClick={() => {
                    if (item.disabled) return;
                    onNavigate(item.locked ? '/login' : item.path);
                  }}
                  aria-disabled={item.disabled ? 'true' : undefined}
                  className={`
                    flex items-center gap-3 w-full px-3 py-2.5 rounded-lg
                    text-sm font-medium transition-colors
                    ${item.disabled
                      ? 'text-text-muted/30 cursor-not-allowed'
                      : item.locked
                        ? 'text-text-muted/40 hover:text-text-muted/60 hover:bg-bg-hover/50 cursor-default'
                        : active
                          ? 'bg-accent-muted text-accent'
                          : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                    }
                  `}
                  title={tooltip}
                >
                  <SidebarNavIcon icon={item.icon} />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left">{item.label}</span>
                      {item.locked && !item.disabled && <SidebarLockIcon />}
                      {item.disabled && <SidebarLockIcon />}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

export function SidebarControls({ collapsed }: { collapsed: boolean }) {
  const setCollapsed = useSettingsStore((s) => s.setSidebarCollapsed);
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="p-2 border-t border-sidebar-border space-y-1">
      <button
        onClick={toggle}
        className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors"
        title={collapsed ? `Switch to ${theme === 'dark' ? 'light' : 'dark'} mode` : undefined}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {theme === 'dark' ? <circle cx="12" cy="12" r="5" /> : <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />}
          {theme === 'dark' && (
            <>
              <line x1="12" y1="1" x2="12" y2="3" />
              <line x1="12" y1="21" x2="12" y2="23" />
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
              <line x1="1" y1="12" x2="3" y2="12" />
              <line x1="21" y1="12" x2="23" y2="12" />
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </>
          )}
        </svg>
        {!collapsed && <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
      </button>

      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors"
      >
        <svg
          className={`w-5 h-5 transition-transform ${collapsed ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="11,17 6,12 11,7" />
          <polyline points="18,17 13,12 18,7" />
        </svg>
        {!collapsed && <span>Collapse</span>}
      </button>

      {/* Terms / Privacy — anchored at the very bottom with a bare separator */}
      {!collapsed && (
        <>
          <hr className="border-sidebar-border mx-1 my-1" />
          <div className="flex justify-center gap-3 px-3 py-1">
            <button
              onClick={() => navigate('/terms')}
              className="text-[10px] text-text-muted hover:text-text-secondary transition-colors"
            >
              Terms
            </button>
            <span className="text-[10px] text-text-muted">&middot;</span>
            <button
              onClick={() => navigate('/privacy')}
              className="text-[10px] text-text-muted hover:text-text-secondary transition-colors"
            >
              Privacy
            </button>
          </div>
        </>
      )}
    </div>
  );
}
